import { useMemo, useState } from 'react';
import { categories, freshNews } from '@/mocks/chaoyang';
import { formatCount, toneBadge, toneDot } from './tones';

export default function NewsFeed() {
  const [active, setActive] = useState('all');

  const filtered = useMemo(() => (active === 'all' ? freshNews : freshNews.filter((n) => n.category === active)), [active]);

  return (
    <section id="fresh" className="scroll-mt-24 bg-[#fff8ee] py-10 md:py-14">
      <div className="chigua-container">
        <div className="flex flex-wrap items-end justify-between gap-3 pb-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-500/25">
              <i className="ri-flashlight-fill text-sm"></i>
              栏目专区 · 今日新瓜
            </span>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-stone-900 md:text-3xl">今日新瓜</h2>
            <p className="mt-1 text-sm text-stone-600">最新发回的现场与爆料，按栏目筛选，看上哪条点进去追后续。</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActive('all')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors cursor-pointer ${
              active === 'all' ? 'bg-orange-600 text-white shadow-[0_6px_16px_rgba(232,88,43,0.28)]' : 'bg-white text-stone-600 ring-1 ring-black/10 hover:text-orange-700'
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.id === active ? 'all' : c.id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                active === c.id ? 'bg-orange-600 text-white shadow-[0_6px_16px_rgba(232,88,43,0.28)]' : 'bg-white text-stone-600 ring-1 ring-black/10 hover:text-orange-700'
              }`}
            >
              <i className={`${c.icon} text-sm`}></i>
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {filtered.length === 0 && (
              <div className="rounded-2xl bg-white p-8 text-center ring-1 ring-black/5">
                <i className="ri-emotion-sad-line text-3xl text-stone-300"></i>
                <p className="mt-2 text-sm text-stone-500">该栏目暂时还没有新瓜，看看其他栏目吧。</p>
                <button type="button" onClick={() => setActive('all')} className="mt-3 text-sm font-semibold text-orange-600 hover:underline cursor-pointer">
                  查看全部今日新瓜
                </button>
              </div>
            )}

            {filtered.map((n, idx) => (
              <article key={n.id} className="group flex items-stretch gap-0 rounded-2xl bg-white ring-1 ring-black/5 transition-shadow hover:ring-orange-500/30">
                <div className={`flex w-14 shrink-0 items-center justify-center rounded-l-2xl text-2xl font-black ${n.hot ? 'bg-gradient-to-b from-red-500 to-orange-500 text-white' : 'bg-black/[0.03] text-stone-300 group-hover:text-orange-400'}`}>
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <div className="min-w-0 flex-1 p-4 md:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-bold ring-1 ${toneBadge[n.tone]}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${toneDot[n.tone]}`}></span>
                      {n.tag}
                    </span>
                    <span className="rounded-md bg-black/[0.04] px-2 py-0.5 text-[11px] font-medium text-stone-600">{n.category}</span>
                    {n.hot && (
                      <span className="chigua-pulse inline-flex items-center gap-1 rounded-md bg-red-500/10 px-2 py-0.5 text-[11px] font-bold text-red-600">
                        <i className="ri-fire-fill"></i>正在发酵
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 text-[15px] font-bold leading-snug text-stone-900 md:text-lg">
                    <a href="#fresh" className="cursor-pointer hover:text-orange-700" onClick={(e) => e.preventDefault()}>
                      {n.title}
                    </a>
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-stone-600">{n.summary}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-400">
                    <span className="inline-flex items-center gap-1 font-medium text-stone-500">
                      <i className="ri-map-pin-2-line text-orange-500"></i>
                      {n.area}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {n.time}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatCount(n.views)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {n.comments}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl bg-white p-5 ring-1 ring-orange-500/10">
              <h3 className="flex items-center gap-2 text-base font-black text-stone-900">
                <i className="ri-apps-2-line text-lg text-orange-500"></i>
                栏目分布
              </h3>
              <p className="mt-1 text-xs text-stone-400">按栏目浏览，直击你感兴趣的现场。</p>
              <ul className="mt-4 space-y-2">
                {categories.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setActive(c.id === active ? 'all' : c.id)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors cursor-pointer ${
                        active === c.id ? 'bg-orange-500/10' : 'hover:bg-black/5'
                      }`}
                    >
                      <span className={`flex h-9 w-9 items-center justify-center rounded-lg text-base ${active === c.id ? 'bg-orange-600 text-white' : 'bg-orange-500/10 text-orange-600'}`}>
                        <i className={c.icon}></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-stone-800">{c.name}</span>
                        <span className="block truncate text-[11px] text-stone-400">{c.desc}</span>
                      </span>
                      <span className="rounded-md bg-black/5 px-2 py-0.5 text-xs font-bold text-stone-500">{c.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="overflow-hidden rounded-2xl bg-[#2a1a10] p-5 text-white">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-amber-300">
                <i className="ri-megaphone-line"></i>
                瓜友征集令
              </span>
              <h3 className="mt-3 text-lg font-black leading-snug">发现猛料？欢迎来投稿！</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">注明时间、地点与大致经过，附上你允许发布的现场图片，我们核实后统一整理。</p>
              <a href="#contact" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-sm font-bold text-stone-900 transition-colors hover:bg-amber-300">
                我要投稿
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}